import { defineStore } from 'pinia';
import { usePinia } from '#imports';
import { handleErrorAndNotificationHandler } from '~/util/errorNotificationHandler';
import type { Pinia } from './shared/sharedTypes';

export interface Action {
  href: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
}

export interface ClientActions {
  [actionKey: string]: Action;
}

export interface Address {
  addressId: number;
  contactTypeDisplayName?: string;
  countryId: number;
  countryName: string;
  locality?: string;
  region?: string;
  postalCode?: string;
  primary: boolean;
  buildingName?: string;
  streetAddressLine1?: string;
  streetAddressLine2?: string;
  streetAddressLine3?: string;
  contactType?: string;
}

export interface PhoneNumber {
  phoneId: number;
  contactTypeDisplayName: string;
  number: string;
  primary: boolean;
  extension?: string;
}

export interface BasicUserInfo {
  displayName: string;
  displayPrefix: string;
  displaySuffix: string;
  emailAddress: string;
  firstName: string;
  jobTitle: string;
  languageId: string;
  lastName: string;
  middleName: string;
  portraitUrl?: string;
  prefixId: number;
  suffixId: number;
  timezoneId: string;
  userId: number;
  uuid: string;
}

export interface ContactData {
  address: Address;
  phone: PhoneNumber;
}

export interface ClientAdminContact {
  acceptedTermsOfService: boolean;
  basicUserInfo: BasicUserInfo;
  contactData: ContactData;
  lastLoginDateTimeMillis: number;
  missingInviteAsNewUser: boolean;
  verifiedEmail: boolean;
}

export interface ChannelStudyTeam {
  teamType: string;
}

export interface Channel {
  actions: Record<string, Action>;
  channelRoleCategoryMemberCounts: Record<string, number>;
  channelSalesOrderType: string;
  channelSponsor: {
    name: string;
    organizationId: number;
  };
  channelStatus: string;
  clientChannelId: number;
  customerStudyId: number;
  name: string;
  teamTypeCounts: Record<string, object>;
  channelStudyTeams?: { teamType: string }[];
}

export interface EmailAsset {
  createDateTimeMillis: number;
  currentState: string;
  emailId: number;
  modifiedDateTimeMillis: number;
}

export interface WorkflowStatusOption {
  statusName: string;
  statusNameLabel: string;
  updateStatusUrl: string;
}

export interface Workflow {
  statusOptions: WorkflowStatusOption[];
}

export interface InviteUser {
  displayName: string;
  displayPrefix: string;
  displaySuffix: string;
  emailAddress: string;
  firstName: string;
  jobTitle: string;
  languageId: string;
  lastName: string;
  middleName: string;
  portraitUrl?: string;
  prefixId: number;
  suffixId: number;
  timezoneId: string;
  userId: number;
  uuid: string;
}

export interface InviteItem {
  actions: Record<string, Action>;
  channelId: number | string;
  channelName: string;
  completed: boolean;
  createDateTimeMillis: number;
  email: string;
  emailAsset: EmailAsset;
  expirationDateTimeMillis: number;
  inviteId: number;
  message: string;
  roleIds: number[];
  teamId: number | string;
  teamName: string;
  recipientUserId: number;
  senderUserId: number;
  invitedByUser: InviteUser;
  invitedUser: InviteUser;
  workflow: Workflow;
}

export interface FavoriteChannel {
  favoriteChannelId: number | string;
  channelId: number | string;
  userId: number;
  createDateTimeMillis: number;
  uuid: string;
}

export interface ClientOrganization {
  clientId: number;
  city?: string;
  country?: string;
  createDateTimeMillis: number;
  entityType: string;
  isPrivate: boolean;
  logoId: number;
  modifiedDateTimeMillis: number;
  name: string;
  organizationId: number;
  userId: number;
}

export interface MembersActions {
  invites: {
    items: InviteItem[];
    totalCount: number;
  };
}

export interface ClientChannelMap {
  [clientId: number]: Channel[];
}

export interface GroupedStudies {
  customersAndStudies?: CustomersAndStudy[];
}

export interface CustomersAndStudy {
  customerId: number;
  customerName: string;
  studies: GroupedStudy[];
}

export interface GroupedStudy {
  studyId: number;
  studyName: string;
}

export interface Client {
  clientId: number;
  clinicalTrialEntityId: number;
  name: string;
  organizationId: number;
  uuid: string;
  addresses: Address[];
  phoneNumbers: PhoneNumber[];
  favoriteChannels: FavoriteChannel[];
  actions: ClientActions;
  adminUserIds: number[];
  clientAdminContacts: ClientAdminContact[];
  channelsOwnedByClients: {
    items: Channel[];
  };
}

interface AcceptDeclineResponse {
  status: string;
  message?: string;
}

export interface ClientStoreState {
  client: Client | null;
  favoriteChannels: FavoriteChannel[];
  channelsOwnedByClients: {
    items: Channel[];
  };
  clientAdminContacts: ClientAdminContact[];
  membersActions: MembersActions;
  channelsStudyTeams: Channel[];
  memberInvitations: {
    [key: string]: InviteItem;
  };
  emailEvents: {
    [key: string]: {
      eventId: number;
      eventType: string;
      emailId: number;
      timestamp: number;
    };
  };
  clientOrganizations: ClientOrganization[];
  clientChannelHashMap: ClientChannelMap;
  invitationStatus?: AcceptDeclineResponse;
  allGroupedStudies: GroupedStudies;
}

interface Invite {
  inviteId: number;
}

interface AcceptDeclineParams {
  [key: string]: unknown;
}
interface FetchMemberInvitationsParams {
  teamId?: number | string;
  userId?: number;
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: string;
}

interface LatestEmailEventParams {
  channelId: number | string;
  memberId: number;
  emailId: number;
  page?: number;
  pageSize?: number;
}

interface PiniaAPI {
  app: {
    getAllGroupedStudies(queryParams: Record<string, unknown>): string;
  };
  user: {
    favoriteChannelsActions(channelId: number | string): string;
    getMembershipActions(queryParams: Record<string, unknown>): string;
    updateUserProfile(): string;
  };
  clients: {
    getClient(clientId: number): string;
    getChannelsOwnedByClient(
      clientId: number,
      queryParams: Record<string, unknown>
    ): string;
    getClientAdminContacts(clientId: number): string;
    postChannelAdmin(queryParams: Record<string, unknown>): string;
    clientStudyTeamsForChannels(clientId: number, queryParams: string): string;
    removeClientAdministrator(clientId: number, adminUserId: number): string;
    addClientAdministrator(clientId: number): string;
    searchClientOrganizations(
      clientId: number,
      queryParams: Record<string, unknown>
    ): string;
  };
  comments: {
    searchChannelInvitation(queryParams: Record<string, unknown>): string;
  };
  channels: {
    statusEmailForMember(
      channelId: number | string,
      memberId: number,
      queryParams: Record<string, unknown>
    ): string;
  };
}

export const useClientStore = defineStore('client', {
  state: (): ClientStoreState => ({
    client: reactive<Client>({} as Client),
    favoriteChannels: [],
    channelsOwnedByClients: { items: [] },
    clientAdminContacts: [],
    membersActions: {
      invites: {
        items: [],
        totalCount: 0,
      },
    },
    channelsStudyTeams: [],
    memberInvitations: {},
    emailEvents: {},
    clientOrganizations: [],
    clientChannelHashMap: {},
    allGroupedStudies: {},
  }),
  actions: {
    async fetchAllGroupedStudies(search: string) {
      const pinia = usePinia() as Pinia<PiniaAPI>;
      const Query = pinia.$Query;
      try {
        const resp: GroupedStudies = await pinia.$axios.$get(
          pinia.$API.app.getAllGroupedStudies(
            new Query({
              search,
            }).getQueryParameters()
          )
        );
        this.allGroupedStudies = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in adding client Administrator',
          error
        );
      }
    },
    async getSingleClient(clientId: number) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        const resp = (await pinia.$axios.$get(
          pinia.$API.clients.getClient(clientId)
        )) as Client;
        this.client = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error getting single client:',
          error
        );
      }
    },
    async createNewChannel({
      client,
      params,
    }: {
      client: {
        createChannel: (params: {
          studyName: string;
          clientId: number;
        }) => Promise<void>;
      };
      params: {
        studyName: string;
        clientId: number;
      };
    }) {
      try {
        await client.createChannel(params);
      } catch (error) {
        handleErrorAndNotificationHandler('Error creating new channel:', error);
      }
    },

    async addFavoriteChannel(channelId: number | string) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        await pinia.$axios.$post(
          pinia.$API.user.favoriteChannelsActions(channelId)
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error adding channel to favorites:',
          error
        );
      }
    },
    async removeFavoriteChannel(channelId: number | string) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        await pinia.$axios.$delete(
          pinia.$API.user.favoriteChannelsActions(channelId)
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error removing channel from favorites:',
          error
        );
      }
    },
    async getClientOwnedChannels({
      clientId,
      page,
      pageSize,
    }: {
      clientId: number;
      page: number;
      pageSize: number;
    }) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$get<Client['channelsOwnedByClients']>(
          pinia.$API.clients.getChannelsOwnedByClient(
            clientId,
            new Query({ page, pageSize }).getQueryParameters()
          )
        );

        this.channelsOwnedByClients = { items: resp.items };
        this.clientChannelHashMap = {
          ...this.clientChannelHashMap,
          [clientId]: resp.items,
        };
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching client owned channels:',
          error
        );
      }
    },

    async getAdminContactsForClient(clientId: number) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        const resp = await pinia.$axios.$get<ClientAdminContact[]>(
          pinia.$API.clients.getClientAdminContacts(clientId)
        );
        this.clientAdminContacts = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching admin contacts for a client:',
          error
        );
      }
    },

    async addChannelAdminToTeam({
      channelId,
      clientId,
      teamId,
    }: {
      channelId: number | string;
      clientId: number;
      teamId: number | string;
    }) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        const Query = pinia.$Query;
        await pinia.$axios.$post(
          pinia.$API.clients.postChannelAdmin(
            new Query({ clientId, channelId, teamId }).getQueryParameters()
          )
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error adding channel admin to team:',
          error
        );
      }
    },
    async getMembersActions({
      search,
      sort,
      actionType,
    }: {
      search?: string;
      sort?: string;
      actionType?: string;
    }) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$get<MembersActions>(
          pinia.$API.user.getMembershipActions(
            new Query({ search, sort, actionType }).getQueryParameters()
          )
        );
        this.membersActions = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching members actions:',
          error
        );
      }
    },

    async acceptDeclineInvitation({
      allInvites,
      params,
    }: {
      allInvites: {
        acceptDecline(
          params: AcceptDeclineParams
        ): Promise<AcceptDeclineResponse | undefined>;
      };
      params: AcceptDeclineParams;
    }) {
      const resp = await allInvites.acceptDecline(params);
      if (resp) {
        (
          this as unknown as { invitationStatus?: AcceptDeclineResponse }
        ).invitationStatus = resp;
      }
    },
    async getClientStudyTeamForChannels({
      clientId,
      typeOfTeam,
      channelId,
    }: {
      clientId: number;
      typeOfTeam: string[];
      channelId: number | string;
    }) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;

        const teamTypes = typeOfTeam
          .map(
            (t, i) => `${i === 0 ? '?' : '&'}teamType=${encodeURIComponent(t)}`
          )
          .join('');

        const query = `${teamTypes}&teamStatusType=OPEN&channelId=${channelId}`;

        type ApiResponseItem = {
          actions: Record<string, Action>;
          channelRoleCategoryMemberCounts: Record<string, number>;
          channelSalesOrderType: string;
          channelSponsor: {
            name: string;
            organizationId: number;
          };
          channelStatus: string;
          clientChannelId: number;
          customerStudyId: number;
          name: string;
          teamTypeCounts: Record<string, object>;
          channelStudyTeams?: {
            teamType: string;
          }[];
        };

        const resp = await pinia.$axios.$post<{ items: ApiResponseItem[] }>(
          pinia.$API.clients.clientStudyTeamsForChannels(clientId, query),
          []
        );

        resp.items.sort((a, b) => {
          const aTeamType = a.channelStudyTeams?.[0]?.teamType ?? '';
          const bTeamType = b.channelStudyTeams?.[0]?.teamType ?? '';
          return aTeamType.localeCompare(bTeamType);
        });

        this.channelsStudyTeams = resp.items;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching Client Study Team For Channels:',
          error
        );
      }
    },

    async fetchMemberInvitations({
      teamId,
      userId,
      page,
      pageSize,
      search,
      sort,
    }: FetchMemberInvitationsParams) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$post<{ [key: string]: InviteItem }>(
          pinia.$API.comments.searchChannelInvitation(
            new Query({
              teamId,
              userId,
              page,
              pageSize,
              search,
              sort,
            }).getQueryParameters()
          ),
          []
        );
        this.memberInvitations = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching member invitations:',
          error
        );
      }
    },

    async latestEmailEvent({
      channelId,
      memberId,
      emailId,
      page,
      pageSize,
    }: LatestEmailEventParams) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$get(
          pinia.$API.channels.statusEmailForMember(
            channelId,
            memberId,
            new Query({ emailId, page, pageSize }).getQueryParameters()
          )
        );
        const key = emailId;
        this.emailEvents = {
          ...this.emailEvents,
          [key]: resp,
        };
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching latest email event:',
          error
        );
      }
    },
    async searchClientOrganizations({
      clientId,
      sponsorName,
    }: {
      clientId: number;
      sponsorName: string;
    }) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;
        const Query = pinia.$Query;
        const resp = await pinia.$axios.$post<ClientOrganization[]>(
          pinia.$API.clients.searchClientOrganizations(
            clientId,
            new Query({ keywords: sponsorName }).getQueryParameters()
          )
        );
        this.clientOrganizations = resp;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in search client Organizations:',
          error
        );
      }
    },

    removeInvite(payload: number) {
      if (!this.membersActions.invites?.items) return;
      this.membersActions.invites.items =
        this.membersActions.invites.items.filter(
          (i: Invite) => i.inviteId !== payload
        );
    },
    async removeClientAdministrator(clientId: number, adminUserId: number) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;

        await pinia.$axios.$delete(
          pinia.$API.clients.removeClientAdministrator(clientId, adminUserId)
        );

        await this.getAdminContactsForClient(clientId);

        return true;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in removing client Administrator:',
          error
        );

        return false;
      }
    },
    async addClientAdministrator(admin: {
      firstName: string;
      lastName: string;
      emailAddresses: string;
      clientId: number;
    }) {
      try {
        const pinia = usePinia() as Pinia<PiniaAPI>;

        const requestBody = {
          firstName: admin.firstName.trim(),
          lastName: admin.lastName.trim(),
          emailAddress: admin.emailAddresses.trim(),
          sendNotificationEmail: true,
        };

        await pinia.$axios.$post(
          pinia.$API.clients.addClientAdministrator(admin.clientId),
          requestBody
        );

        await this.getAdminContactsForClient(admin.clientId);

        return true;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in adding client Administrator',
          error
        );

        return false;
      }
    },
  },
  getters: {
    getTeamsInChannelGetter: (state) => state.channelsStudyTeams,
    getMembersActionsGetter: (state) => state.membersActions,
    getContactAdministratorsGetter: (state) => state.clientAdminContacts,
  },
});
